import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ProjectVehicleService from "../services/ProjectVehicleService";
import Breadcrumb from "../components/Breadcrumb";
import ReactPaginate from "react-paginate";
import StorageService from "../services/StorageService";
import usePermission from "../utils/UsePermission";
import ScreenData from "../config/ScreenData";

export default function ViewProjectVehicle() {
  const [vehicles, setVehicles] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("info");
  const messageTimer = useRef(null);
  const navigate = useNavigate();

  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const companyId = StorageService.activeCompany()?.companyId;
      const res = await ProjectVehicleService.getAll(currentPage, itemsPerPage, "", null, null, companyId);
      if (res.status) {
        const formatted = (res.data || []).map((v) => ({
          ...v,
          isActive: v.status === "Active",
        }));
        setVehicles(formatted);
        setPageCount(res.pagination?.totalPages || 0);
      } else {
        showMessage("Failed to fetch project vehicles", "error");
      }
    } catch (err) {
      showMessage("Error fetching project vehicles", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, [currentPage]);

  const handlePageClick = (event) => {
    const _cp = event.selected + 1;
    setCurrentPage(_cp);
  };

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    if (messageTimer.current) clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  const closeMessage = () => {
    setMessage("");
    if (messageTimer.current) clearTimeout(messageTimer.current);
  };

  const { permission, can } = usePermission(
    ScreenData.Project_Vehicle,
    showMessage
  );

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelected(vehicles.map((v) => v.id));
    else setSelected([]);
  };

  const handleAdd = () => navigate("/cpnl/projectvehicle/add");

  const handleModify = () => {
    if (selected.length === 1) {
      const item = vehicles.find((v) => v.id === selected[0]);
      localStorage.setItem("editProjectVehicle", JSON.stringify(item));
      navigate("/cpnl/projectvehicle/add");
    } else if (!selected.length)
      showMessage("Select a project vehicle to modify");
    else showMessage("Select only one project vehicle to modify");
  };

  const handleToggleStatus = async (id, currentStatus) => {
    if (!can("canModify")) {
      showMessage("You don't have permission to modify status", "error");
      return;
    }
    const newStatusText = currentStatus ? "Inactive" : "Active";

    const confirmed = window.confirm(
      `Are you sure you want to mark this Project Vehicle as ${newStatusText}?`
    );
    if (!confirmed) return;

    try {
      const res = await ProjectVehicleService.updateStatus(id);

      if (res && res.status === false) {
        showMessage(res.message || "Failed to update status", "error");
        return;
      }

      setVehicles((prev) =>
        prev.map((v) => (v.id === id ? { ...v, isActive: !currentStatus } : v))
      );

      showMessage(
        (res && res.message) || `Vehicle status updated to ${newStatusText}`,
        "success"
      );
    } catch (error) {
      console.error("Status update failed:", error);
      const serverMsg =
        error?.response?.data?.message || "Failed to update status";
      showMessage(serverMsg, "error");
    }
  };

  return (
    <>
      <Breadcrumb
        pageTitle="Project Vehicles"
        secondTitle="Projects"
      ></Breadcrumb>
      <div className="row">
        <div className="card py-2 bg-white shadow-sm">
          {message && (
            <div
              className={`alert ${msgType === "success" ? "alert-success" : "alert-danger"
                } d-flex justify-content-between align-items-center mb-2 p-2`}
              style={{ borderRadius: "5px" }}
            >
              <span>{message}</span>
              <button
                type="button"
                className="btn-close"
                onClick={closeMessage}
              ></button>
            </div>
          )}

          <div className="mb-3 pb-2 border-bottom">
            <button
              className="d-inline-flex btn btn-success me-2"
              onClick={handleAdd}
              disabled={loading}
            >
              <i class="ti ti-plus me-1"></i> Add
            </button>
            <button
              className="d-inline-flex btn btn-warning me-2"
              onClick={handleModify}
              disabled={loading}
            >
              <i class="ti ti-pencil me-1"></i> Modify
            </button>
            {/* <button
              className="btn btn-danger btn-sm me-2"
              onClick={handleDelete}
              disabled={loading}
            >
              Delete
            </button> */}
          </div>

          <div className="table-responsive">
            <table className="table table-striped table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        selected.length === vehicles.length &&
                        vehicles.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  {/* <th>Company</th> */}
                  <th>Project</th>
                  <th>Vehicle Type</th>
                  <th>Vehicle</th>
                  <th>Start Date</th>
                  <th>Status</th>
                  {/* <th>Action</th> */}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      ></div>
                    </td>
                  </tr>
                ) : vehicles.length ? (
                  vehicles.map((v) => (
                    <tr key={v.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.includes(v.id)}
                          onChange={() => handleSelect(v.id)}
                        />
                      </td>
                      {/* <td>{v.companyName || "N/A"}</td> */}
                      <td>{v.projectName || "N/A"}</td>
                      <td>{v.vehicleType || "N/A"}</td>
                      <td>{v.vehicleName || "N/A"}</td>
                      <td>
                        {v.startDate
                          ? new Date(v.startDate).toLocaleString()
                          : "N/A"}
                      </td>
                      <td>
                        <span
                          className={`badge ${v.isActive ? "bg-success" : "bg-secondary"
                            }`}
                          onClick={() => handleToggleStatus(v.id, v.isActive)}
                          style={{ cursor: can("canModify") ? "pointer" : "default" }}
                        >
                          {v.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      {/* <td>
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => toggleStatus(v.id)}
                      >
                        {v.status === "Active" ? "Inactivate" : "Activate"}
                      </button>
                    </td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-muted">
                      No project vehicles found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <ReactPaginate
              forcePage={currentPage - 1}
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={handlePageClick}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={pageCount}
              previousLabel="< Previous"
              containerClassName="pagination-container"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              activeClassName="active"
            />
          </div>


          {/* <ReactPaginate
            forcePage={currentPage - 1}
            breakLabel="..."
            nextLabel="Next >"
            previousLabel="< Previous"
            onPageChange={(e) => setCurrentPage(e.selected + 1)}
            pageCount={pageCount}
            pageRangeDisplayed={3}
            marginPagesDisplayed={2}
            containerClassName="pagination-container"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            activeClassName="active"
          /> */}




        </div>
      </div>
    </>
  );
}
